import { MakerJSDownload, MimeType } from "@project/emerge";
import maxBy from "lodash/maxBy";
import MakerJS from "makerjs";
import { MaxRectsPacker } from "maxrects-packer";
import type { OuterPanelProperties } from "../interfaces/OuterPanelProperties";
import { RectPropertiesBySide } from "../structure/RectProperties";

// 6mm tool + 1mm extra for cw/ccw cutting
const PACKING_PADDING = 11 / 2;

interface PanelData {
  panel: OuterPanelProperties;
  originalWidth: number;
  originalHeight: number;
}

/**
 * Creates a DXF download for a group of panels with the same material/thickness
 * Uses MaxRects bin-packing algorithm for optimal panel placement
 */
export class PanelDownloadGenerator {
  private readonly panels: OuterPanelProperties[];
  private readonly title: string;

  constructor(panels: OuterPanelProperties[], title: string) {
    if (panels.length === 0) {
      throw new Error(
        "Cannot create PanelDownloadGenerator with empty panels array",
      );
    }
    this.panels = panels;
    this.title = title;
  }

  /**
   * Generate a Download object for these panels
   */
  public async generateDownload(): Promise<PanelMakerJSDownload> {
    return new PanelMakerJSDownload(this.title, MimeType.DXF, this.panels);
  }
}

// Gap between boards in the DXF output
const BIN_GAP = 30;

/**
 * MakerJS-based download implementation for panels
 * Uses MaxRects algorithm for optimal bin-packing
 */
class PanelMakerJSDownload extends MakerJSDownload {
  private readonly panels: OuterPanelProperties[];

  constructor(
    title: string,
    mimeType: MimeType.DXF | MimeType.SVG,
    panels: OuterPanelProperties[],
  ) {
    super(title, mimeType);
    this.panels = panels;
    this.packPanels();
  }

  /**
   * Pack panels onto the board using MaxRects bin-packing algorithm
   * Provides ~90-95% packing efficiency with automatic rotation
   */
  private packPanels(): void {
    const firstPanel = this.panels[0];
    if (!firstPanel) return;

    const { packer, boardHeight } = this.createPacker(firstPanel);

    // Add all panels to packer
    for (const panel of this.panels) {
      packer.add(
        panel.size.x + PACKING_PADDING * 2,
        panel.size.y + PACKING_PADDING * 2,
        {
          panel,
          originalWidth: panel.size.x,
          originalHeight: panel.size.y,
        } as PanelData,
      );
    }

    // Process and place packed panels
    let layerNumber = 0;
    for (let binIndex = 0; binIndex < packer.bins.length; binIndex++) {
      const bin = packer.bins[binIndex];
      if (!bin) continue;

      const binOffsetY =
        binIndex === 0 ? 0 : -(boardHeight + BIN_GAP) * binIndex;

      for (const rect of bin.rects) {
        layerNumber++;
        this.placePanel(rect, binIndex, layerNumber, binOffsetY);
      }
    }
  }

  /**
   * Create and configure the MaxRects packer
   */
  private createPacker(firstPanel: OuterPanelProperties) {
    const boardSize = firstPanel.color?.boardSize ?? [2500, 1250];
    const maxWidth = maxBy(this.panels, "size.x")?.size.x ?? 0;
    const boardW = boardSize[0] ?? 2500;
    const boardH = boardSize[1] ?? 1250;
    const w = maxWidth > boardW ? boardH : boardW;
    const h = maxWidth > boardW ? boardW : boardH;

    const packer = new MaxRectsPacker(w, h, PACKING_PADDING, {
      smart: true,
      pot: false,
      square: false,
      allowRotation: true,
      tag: false,
      border: 0,
    });

    return { packer, boardHeight: h };
  }

  /**
   * Place a single panel in the model
   */
  private placePanel(
    rect: { x: number; y: number; rot: boolean; data: unknown },
    binIndex: number,
    layerNumber: number,
    binOffsetY: number,
  ): void {
    const panelData = rect.data as PanelData;
    const panel = panelData.panel;
    const isRotated = rect.rot;

    const panelModel = this.createPanelModel(panel);
    const subModel = MakerJS.model.center(
      MakerJS.model.mirror(panelModel, false, true),
    );

    subModel.layer = `${layerNumber} ${panel.size.x}mm x ${panel.size.y}mm (Platte ${binIndex + 1})`;

    if (isRotated) {
      MakerJS.model.rotate(subModel, 90);
    }

    const rectX = rect.x + PACKING_PADDING;
    const rectY = rect.y + PACKING_PADDING + binOffsetY;
    const halfW = isRotated ? panel.size.y / 2 : panel.size.x / 2;
    const halfH = isRotated ? panel.size.x / 2 : panel.size.y / 2;

    this.model.addSubModel(subModel, rectX + halfW, rectY + halfH);
  }

  /**
   * Create a MakerJS model for a single panel with cutouts using boolean subtraction
   * Matches the old Panel.createMakerJsModel implementation
   */
  private createPanelModel(panel: OuterPanelProperties): MakerJS.IModel {
    const { cutouts, side, size, position } = panel;

    // Start with a rectangle
    let result: MakerJS.IModel = new MakerJS.models.Rectangle(size.x, size.y);

    // Get side-specific transformation properties
    const rectProps = RectPropertiesBySide[side];
    if (!rectProps) {
      return MakerJS.model.moveRelative(result, [-size.x / 2, -size.y / 2]);
    }

    const { horizontal, horizontalDir, vertical, verticalDir } = rectProps;

    // Apply cutouts using boolean subtraction
    for (const cutout of cutouts) {
      if (!cutout.path) continue;

      try {
        // Calculate cutout position relative to panel
        const dx =
          (cutout.position[horizontal] - position[horizontal]) *
            horizontalDir[horizontal] +
          size.x / 2;
        const dy =
          (-cutout.position[vertical] + position[vertical]) *
            verticalDir[vertical] +
          size.y / 2;

        // Import the SVG path
        let contourModel = MakerJS.importer.fromSVGPathData(cutout.path);

        // Apply transformations: mirror, scale, rotate, translate
        contourModel = MakerJS.model.mirror(contourModel, false, true);
        contourModel = MakerJS.model.scale(contourModel, 1, true);

        if (cutout.orientation && cutout.orientation !== 0) {
          contourModel = MakerJS.model.rotate(
            contourModel,
            cutout.orientation,
            [0, 0],
          );
        }

        contourModel = MakerJS.model.moveRelative(contourModel, [dx, dy]);

        // Boolean subtraction
        result = MakerJS.model.combineSubtraction(result, contourModel);
      } catch (error) {
        console.warn("Failed to process cutout:", cutout.path, error);
      }
    }

    // Center the result
    return MakerJS.model.moveRelative(result, [-size.x / 2, -size.y / 2]);
  }
}
